import { Performance } from "@/assets/js/tactics/pitch/responsibilities";
import Strata from "@/assets/js/strata";
import PlaymakerTypes from "@/assets/js/playmaker-types";
import PositionGroups from "@/assets/js/position-groups";
import Partner from "@/assets/js/partner";
import PartnerTypes from "@/assets/js/partner-types";
import PairWith from "@/assets/js/pair-with";
import Duties from '@/assets/js/duties.js';


export default class CentralAttack {
  constructor(position) {
    this.position = position;
  }

  get responsibilityPerformance() {
    return {
      defensivePressure: this.defensivePressure(),
      creatingChances: this.creatingChances(),
      stretchingPlay: this.stretchingPlay(),
      takingChances: this.takingChances(),
    };
  }

  defensivePressure() {
    const performs = this.position.positionGroup === PositionGroups.centralAttack || 
        this.position.positionGroup === PositionGroups.attackingMidfield ||
        (this.position.positionGroup === PositionGroups.wingForward && this.position.playerInstructions.pressingIntensity > 0);
    return new Performance(performs, false, false);
  }

  creatingChances() {
    const positionOkay = [Strata.M, Strata.AM, Strata.ST].includes(this.position.stratum) ||
        (this.position.stratum === Strata.DM && this.position.playerInstructions.getFurtherForward === 1);
    
    const performs = positionOkay &&
        (this.position.playerInstructions.passingRisks === 1 || this.position.playerInstructions.holdUpBall === 1) &&
        this.position.playerInstructions.passingRisks >= 0 &&
        this.position.playerInstructions.getFurtherForward < 1 &&
        this.position.playerInstructions.passingDirectness >= 0 &&
        this.position.playerInstructions.creativeFreedom >= 0;

    const contributes = positionOkay &&
        this.position.playerInstructions.passingRisks >= 0 &&
        this.position.playerInstructions.passingDirectness >= 0 &&
        this.position.playerInstructions.creativeFreedom >= 0 &&
        this.position.playerInstructions.dribbling >= 0;

    if (performs && this.position.playerInstructions.passingRisks === 1) this.playmakerType = PlaymakerTypes.number10;
    if (performs && this.position.playerInstructions.holdUpBall === 1) this.playmakerType = PlaymakerTypes.targetMan;

    return new Performance(performs, !performs && contributes, false);
  }

  stretchingPlay() {
    const performs = 
        [Strata.AM, Strata.ST].includes(this.position.stratum) &&
        this.position.playerInstructions.getFurtherForward === 1;

    const contributes = 
        ([Strata.AM, Strata.ST].includes(this.position.stratum) && this.position.playerInstructions.getFurtherForward === 0) ||
        this.position.stratum === Strata.M && this.position.playerInstructions.getFurtherForward === 1;

    const atypical = this.position.playerInstructions.dribbling < 0;

    return new Performance(performs, contributes, atypical);
  }

  takingChances() {
    const performs = [Strata.ST, Strata.AM].includes(this.position.stratum) || 
        (this.position.stratum === Strata.M && this.position.playerInstructions.movement >= 0);

    const atypical = (this.position.stratum === Strata.M && this.position.playerInstructions.movement < 1) || 
        this.position.playerInstructions.movement < 0 || 
        this.position.playerInstructions.dribbling < 1 || 
        this.position.playerInstructions.dropDeepMore < 1;

    return new Performance(performs, false, atypical);
  }

  get partnerability() {
    return {
      highRiskDirect: this.highRiskDirect(),
      highRiskOverlapping: this.highRiskOverlapping(),
      interchanging: this.interchanging(),
      mediumRiskDirect: this.mediumRiskDirect(),
      mediumRiskOverlapping: this.mediumRiskOverlapping(),
    };
  }

  highRiskDirect() {
    if (!this._possiblePositionalArea()) return false;

    if (this.position.playerInstructions.getFurtherForward > 0) {
      return new Partner(
        PartnerTypes.attacking,
        this.position.stratum,
        new PairWith(
          PartnerTypes.ancillary,
          Strata.M,
          this.position.stratum
        )
      );
    }
    
    if (this.position.playerInstructions.movement >= 0) {
      return new Partner(
        PartnerTypes.ancillary,
        this.position.stratum,
        new PairWith(
          PartnerTypes.attacking,
          this.position.stratum,
          Strata.ST
        )
      );
    }

    return false;
  }

  highRiskOverlapping() {
    if (!this._possiblePositionalArea()) return false;
    if (this.position.playerInstructions.movement < 0) return false;

    if (this.position.playerInstructions.getFurtherForward > 0) {
      if (this.position.stratum === Strata.ST) return false;
      return new Partner(
        PartnerTypes.attacking,
        this.position.stratum,
        new PairWith(
          PartnerTypes.ancillary,
          Object.values(Strata).find(stratum => stratum.height === this.position.stratum.height + 1),
          Strata.ST
        )
      );
    }

    return new Partner(
      PartnerTypes.ancillary,
      this.position.stratum,
      new PairWith(
        PartnerTypes.attacking,
        Strata.M,
        Object.values(Strata).find(stratum => stratum.height === this.position.stratum.height - 1)
      )
    );
  }

  interchanging() {
    if (!this._possiblePositionalArea()) return false;

    if (this.position.selectedRole.duty !== Duties.support) return false;
    if (this.position.playerInstructions.movement < 0) return false;

    return new Partner(
      this.position.playerInstructions.getFurtherForward > 0 ? PartnerTypes.overloading : PartnerTypes.balanced,
      this.position.stratum,
      new PairWith(
        this.position.playerInstructions.getFurtherForward > 0 ? PartnerTypes.overloading : PartnerTypes.balanced,
        Strata.M,
        Strata.ST
      )
    );
  }

  mediumRiskDirect() {
    if (!this._possiblePositionalArea()) return false;

    if (this.position.playerInstructions.getFurtherForward > 0) {
      return new Partner(
        PartnerTypes.attacking,
        this.position.stratum,
        new PairWith(
          PartnerTypes.ancillary,
          Strata.M,
          this.position.stratum
        )
      );
    }
    
    if (this.position.playerInstructions.movement < 0) {
      return new Partner(
        PartnerTypes.ancillary,
        this.position.stratum,
        new PairWith(
          PartnerTypes.attacking,
          this.position.stratum,
          Strata.ST
        )
      );
    }

    return false;
  }

  mediumRiskOverlapping() {
    if (!this._possiblePositionalArea()) return false;

    if (this.position.playerInstructions.getFurtherForward > 0) {
      if (this.position.stratum === Strata.ST) return false;
      return new Partner(
        PartnerTypes.attacking,
        this.position.stratum,
        new PairWith(
          PartnerTypes.ancillary,
          Object.values(Strata).find(stratum => stratum.height === this.position.stratum.height + 1),
          Strata.ST
        )
      );
    }
    if (this.position.playerInstructions.movement < 0) {
      return new Partner(
        PartnerTypes.ancillary,
        this.position.stratum,
        new PairWith(
          PartnerTypes.attacking,
          Strata.M,
          Object.values(Strata).find(stratum => stratum.height === this.position.stratum.height - 1)
        )
      );  
    }
    
    return false;
  }

  _possiblePositionalArea() {
    if ([
      PositionGroups.attackingMidfield,
      PositionGroups.centralAttack
    ].includes(this.position.positionGroup)) {
      return true;
    }
    if (this.position.positionGroup === PositionGroups.centralMidfield &&
        (this.position.duty === Duties.attack || this.position.playerInstructions.getFurtherForward > 0)) {
      return true;
    }
  
    if (this.position.positionGroup === PositionGroups.wingForward &&
        (this.position.playerInstructions.attackingWidth < 0 || this.position.playerInstructions.passingRisks > 0)) {
      return true;
    } 
  
    return false;
  }
}


